import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  fetchLiveAuctionRelayStatus,
  fetchLiveAuctionSettings,
  fetchTournamentById,
  fetchTournaments,
  generateLiveAuctionYouTubeStream,
  saveLiveAuctionSettings,
  uploadLiveAuctionTeamLogo,
} from '../../../services/tournamentService';
import { getAuctionManagerPath } from '../../../utils/organization';
import { getAuctionManagerPath as getCommunityAuctionManagerPath, getCommunityId } from '../../../utils/community';
import { getAuctionManagerPath as getClubAuctionManagerPath, getClubId } from '../../../utils/club';
import { copyTextToClipboard } from '../../../utils/clipboard';
import { getPublicLiveAuctionPath, toAbsolutePublicUrl } from '../../../utils/auction/publicAuctionLinks';

const DEFAULT_TEAM_COUNT = 2;
const STREAMING_MODES = {
  DIRECT_MOBILE: 'DIRECT_MOBILE',
  OBS: 'OBS',
  LOCAL_MAC_TESTING: 'LOCAL_MAC_TESTING',
};

const STREAM_PROVIDERS = {
  YOUTUBE: 'YOUTUBE',
  FACEBOOK: 'FACEBOOK',
};

const DEFAULT_FORM = {
  auctionDateTime: '',
  pursePerTeam: '',
  baseBid: '',
  bidIncrement: '',
  playersPerTeam: '',
  auctionProcess: 'auto',
  streamingMode: STREAMING_MODES.DIRECT_MOBILE,
  streamProvider: STREAM_PROVIDERS.YOUTUBE,
  youtubeLiveLink: '',
  livePlaybackUrl: '',
  rtmpIngestUrl: '',
  rtmpStreamKey: '',
  youtubeIngestUrl: 'rtmp://a.rtmp.youtube.com/live2',
  youtubeStreamKey: '',
  youtubeVideoId: '',
};

const isDevLikeEnvironment = () => {
  const nodeEnv = typeof process !== 'undefined' ? process.env?.NODE_ENV : undefined;
  if (nodeEnv && nodeEnv !== 'production') return true;
  if (typeof window === 'undefined') return false;
  const hostname = (window.location?.hostname || '').toLowerCase();
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
};

const createTeam = (index) => ({ name: `Team ${index + 1}`, logoUrl: '', ownerId: '', captain: '', retainers: [] });

const normalizeTeamCount = (value) => Math.max(1, Math.min(12, Number(value) || DEFAULT_TEAM_COUNT));

const formatDateTimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
};

const parsePlayers = (players = []) =>
  players
    .map((player) => ({
      id: player.id || player._id || '',
      name: player.name || player.fullName || 'Unnamed player',
      email: player.email || player.user?.email || '',
      image: player.image || player.profilePhoto || player.media?.profilePhoto || '',
    }))
    .filter((player) => player.id);

const getTeamLogo = (team) => team?.logoUrl || team?.logo || '';

const getTeamInitial = (team, index = 0) =>
  (team?.name || `Team ${index + 1}`).toString().trim().charAt(0).toUpperCase() || 'T';

const buildInitialTeamState = (settingsTeams = [], preferredCount = DEFAULT_TEAM_COUNT) => {
  const teamCount = normalizeTeamCount(preferredCount || settingsTeams.length || DEFAULT_TEAM_COUNT);
  const seeded = settingsTeams.slice(0, teamCount).map((team, index) => ({
    name: team.name || `Team ${index + 1}`,
    logoUrl: team.logoUrl || team.logo || '',
    ownerId: team.ownerId || '',
    captain: team.captain || '',
    retainers: Array.isArray(team.retainers) ? team.retainers.filter(Boolean) : [],
  }));

  while (seeded.length < teamCount) {
    seeded.push(createTeam(seeded.length));
  }

  return { teamCount, teams: seeded, teamSearchTerms: Array.from({ length: teamCount }, () => '') };
};

const LiveAuctionSetupPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { auctionManagerId, organizationId, communityId: routeCommunityId, clubId: routeClubId, tournamentId } = useParams();
  const isIndividualRoute = Boolean(tournamentId && !organizationId && !routeCommunityId && !routeClubId);
  const communityId = getCommunityId(routeCommunityId);
  const clubId = getClubId(routeClubId);

  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState(tournamentId || '');
  const [formState, setFormState] = useState(DEFAULT_FORM);
  const [numberOfTeams, setNumberOfTeams] = useState(DEFAULT_TEAM_COUNT);
  const [teams, setTeams] = useState(() => Array.from({ length: DEFAULT_TEAM_COUNT }, (_, i) => createTeam(i)));
  const [teamSearchTerms, setTeamSearchTerms] = useState(() => Array.from({ length: DEFAULT_TEAM_COUNT }, () => ''));
  const [activeTeamIndex, setActiveTeamIndex] = useState(0);

  const [tournamentPlayers, setTournamentPlayers] = useState([]);
  const [isLoadingTournaments, setIsLoadingTournaments] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [playerError, setPlayerError] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [isGeneratingYouTube, setIsGeneratingYouTube] = useState(false);
  const [relayStatus, setRelayStatus] = useState({ running: false });
  const [copyLinkStatus, setCopyLinkStatus] = useState('');
  const showLocalTestingMode = isDevLikeEnvironment();

  const mappedPlayers = useMemo(() => parsePlayers(tournamentPlayers), [tournamentPlayers]);

  const selectedTournament = useMemo(
    () => tournaments.find((tournament) => tournament.id === selectedTournamentId),
    [tournaments, selectedTournamentId]
  );

  const getManagementPath = useCallback(
    (id) =>
      (isIndividualRoute
        ? `/individual-tournament/${id}/auction`
        : communityId
          ? getCommunityAuctionManagerPath(communityId, auctionManagerId, id)
          : clubId
            ? getClubAuctionManagerPath(clubId, auctionManagerId, id)
            : getAuctionManagerPath(organizationId, auctionManagerId, id)) || '/management/auction',
    [auctionManagerId, clubId, communityId, isIndividualRoute, organizationId]
  );

  useEffect(() => {
    setSelectedTournamentId(tournamentId || '');
  }, [tournamentId]);

  useEffect(() => {
    setActiveTeamIndex((prev) => Math.min(prev, Math.max(0, numberOfTeams - 1)));
  }, [numberOfTeams]);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      setIsLoadingTournaments(true);
      setErrorMessage('');
      try {
        let response;
        if (isIndividualRoute) {
          const tournamentResponse = await fetchTournamentById(tournamentId, token);
          const tournament = tournamentResponse?.tournament || null;
          response = { tournaments: tournament ? [tournament] : [] };
        } else {
          response = await fetchTournaments(token, organizationId, { communityId, clubId });
        }
        const available = (response.tournaments || [])
          .map((tournament) => ({ id: tournament.id || tournament._id, name: tournament.name || 'Untitled tournament' }))
          .filter((tournament) => tournament.id);
        setTournaments(available);

        if (!selectedTournamentId && available.length) {
          const nextTournamentId = available[0].id;
          setSelectedTournamentId(nextTournamentId);
          navigate(`${getManagementPath(nextTournamentId)}/live`, { replace: true });
        }
      } catch (error) {
        setErrorMessage(error.message || 'Unable to load tournaments right now.');
      } finally {
        setIsLoadingTournaments(false);
      }
    };

    load();
  }, [clubId, communityId, getManagementPath, isIndividualRoute, navigate, organizationId, selectedTournamentId, token, tournamentId]);

  useEffect(() => {
    if (!selectedTournamentId || !token) {
      setTournamentPlayers([]);
      return;
    }

    const loadPlayers = async () => {
      setIsLoadingPlayers(true);
      setPlayerError('');
      try {
        const response = await fetchTournamentById(selectedTournamentId, token, organizationId, {
          communityId,
          clubId,
          activeContextType: isIndividualRoute ? 'individual' : undefined,
          ownerType: isIndividualRoute ? 'individual' : undefined,
        });
        setTournamentPlayers(response?.tournament?.players || []);
      } catch (error) {
        setTournamentPlayers([]);
        setPlayerError(error.message || 'Unable to load players for this tournament.');
      } finally {
        setIsLoadingPlayers(false);
      }
    };

    loadPlayers();
  }, [clubId, communityId, isIndividualRoute, organizationId, selectedTournamentId, token]);

  useEffect(() => {
    if (!selectedTournamentId || !token) return;

    const loadSettings = async () => {
      setIsLoadingSettings(true);
      setErrorMessage('');
      setStatusMessage('');
      try {
        const response = await fetchLiveAuctionSettings(selectedTournamentId, token);
        const settings = response.settings;

        if (!settings) {
          setFormState(DEFAULT_FORM);
          const initialTeams = buildInitialTeamState();
          setNumberOfTeams(initialTeams.teamCount);
          setTeams(initialTeams.teams);
          setTeamSearchTerms(initialTeams.teamSearchTerms);
          return;
        }

        setFormState({
          auctionDateTime: formatDateTimeLocal(settings.auctionDateTime),
          pursePerTeam: settings.pursePerTeam ?? '',
          baseBid: settings.baseBid ?? '',
          bidIncrement: settings.bidIncrement ?? '',
          playersPerTeam: settings.playersPerTeam ?? '',
          auctionProcess: settings.auctionProcess || 'auto',
          streamingMode: settings.streamingMode || STREAMING_MODES.DIRECT_MOBILE,
          streamProvider: String(settings.streamProvider || 'youtube').toUpperCase(),
          youtubeLiveLink: settings.youtubeLiveLink || settings.streamUrl || settings.livePlaybackUrl || '',
          livePlaybackUrl: settings.livePlaybackUrl || settings.youtubeLiveLink || '',
          rtmpIngestUrl: settings.rtmpIngestUrl || settings?.streamingConfig?.obs?.ingestUrl || '',
          rtmpStreamKey: settings.rtmpStreamKey || settings?.streamingConfig?.obs?.streamKey || '',
          youtubeIngestUrl: settings.youtubeIngestUrl || settings?.streamingConfig?.youtube?.ingestUrl || 'rtmp://a.rtmp.youtube.com/live2',
          youtubeStreamKey: settings.youtubeStreamKey || settings?.streamingConfig?.youtube?.streamKey || '',
          youtubeVideoId: settings.youtubeVideoId || settings?.streamingConfig?.youtube?.videoId || '',
        });
        setRelayStatus(settings.relayStatus || { running: false });

        const initialTeams = buildInitialTeamState(settings.teams || [], settings.numberOfTeams);
        setNumberOfTeams(initialTeams.teamCount);
        setTeams(initialTeams.teams);
        setTeamSearchTerms(initialTeams.teamSearchTerms);
      } catch (error) {
        setErrorMessage(error.message || 'Unable to load auction settings right now.');
      } finally {
        setIsLoadingSettings(false);
      }
    };

    loadSettings();
  }, [selectedTournamentId, token]);

  useEffect(() => {
    if (!selectedTournamentId || !token) return;
    fetchLiveAuctionRelayStatus(selectedTournamentId, token)
      .then((response) => setRelayStatus(response?.relayStatus || { running: false }))
      .catch(() => setRelayStatus((prev) => prev || { running: false }));
  }, [selectedTournamentId, token]);

  const handleTournamentChange = (event) => {
    const id = event.target.value;
    setSelectedTournamentId(id);
    setStatusMessage('');
    setErrorMessage('');
    navigate(`${getManagementPath(id)}/live`);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((previous) => ({ ...previous, [name]: value }));
  };

  const handleTeamCountChange = (event) => {
    const next = normalizeTeamCount(event.target.value);
    setNumberOfTeams(next);
    setTeams((prev) => {
      const nextTeams = prev.slice(0, next);
      while (nextTeams.length < next) nextTeams.push(createTeam(nextTeams.length));
      return nextTeams;
    });
    setTeamSearchTerms((prev) => {
      const nextTerms = prev.slice(0, next);
      while (nextTerms.length < next) nextTerms.push('');
      return nextTerms;
    });
  };

  const updateTeamField = (teamIndex, field, value) => {
    setTeams((previous) => previous.map((team, idx) => (idx === teamIndex ? { ...team, [field]: value } : team)));
  };

  const toggleRetainer = (teamIndex, playerId) => {
    setTeams((previous) =>
      previous.map((team, idx) => {
        if (idx !== teamIndex) return team;
        const retainers = new Set(team.retainers || []);
        if (retainers.has(playerId)) retainers.delete(playerId);
        else retainers.add(playerId);
        const captain = team.captain === playerId && retainers.has(playerId) ? '' : team.captain;
        return { ...team, captain, retainers: Array.from(retainers) };
      })
    );
  };

  const currentTeam = teams[activeTeamIndex] || null;
  const selectedRetainers = useMemo(() => new Set(currentTeam?.retainers || []), [currentTeam]);

  const captainOptions = useMemo(() => {
    if (!currentTeam) return [];
    return mappedPlayers.filter((player) => player.id === currentTeam.captain || !selectedRetainers.has(player.id));
  }, [currentTeam, mappedPlayers, selectedRetainers]);

  const ownerOptions = mappedPlayers;

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedTournamentId || !token || currentTeam == null) return;

    if (!file.type?.startsWith('image/')) {
      setLogoError('Please select a valid image file.');
      return;
    }

    setLogoUploading(true);
    setLogoError('');
    try {
      const response = await uploadLiveAuctionTeamLogo(
        selectedTournamentId,
        file,
        token,
        getTeamLogo(currentTeam),
      );
      updateTeamField(activeTeamIndex, 'logoUrl', response.logoUrl || '');
    } catch (error) {
      setLogoError(error.message || 'Unable to upload team logo right now.');
    } finally {
      setLogoUploading(false);
    }
  };

  const availablePlayers = useMemo(() => {
    if (!currentTeam) return [];
    const searchTerm = (teamSearchTerms[activeTeamIndex] || '').toLowerCase().trim();
    return mappedPlayers.filter((player) => {
      const reservedIds = new Set([currentTeam.ownerId, currentTeam.captain].filter(Boolean));
      if (reservedIds.has(player.id)) return false;
      if (selectedRetainers.has(player.id)) return false;
      if (!searchTerm) return true;
      return `${player.name} ${player.email}`.toLowerCase().includes(searchTerm);
    });
  }, [activeTeamIndex, currentTeam, mappedPlayers, selectedRetainers, teamSearchTerms]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedTournamentId || !token) return;

    const playersPerTeam = Number(formState.playersPerTeam);
    if (!Number.isFinite(playersPerTeam) || playersPerTeam < 1) {
      setErrorMessage('Players per team must be at least 1.');
      return;
    }

    setIsSaving(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const tournamentPlayerIds = new Set(mappedPlayers.map((player) => player.id).filter(Boolean));
      const teamsForValidation = teams.slice(0, numberOfTeams);
      for (let index = 0; index < teamsForValidation.length; index += 1) {
        const team = teamsForValidation[index];
        if (team.ownerId && !tournamentPlayerIds.has(team.ownerId)) {
          setErrorMessage(`Team ${index + 1}: owner must be a valid tournament player.`);
          setIsSaving(false);
          return;
        }
        if (team.captain && !tournamentPlayerIds.has(team.captain)) {
          setErrorMessage(`Team ${index + 1}: captain must be a valid tournament player.`);
          setIsSaving(false);
          return;
        }
      }

      const payload = {
        ...formState,
        pursePerTeam: formState.pursePerTeam === '' ? 0 : Number(formState.pursePerTeam),
        baseBid: formState.baseBid === '' ? 0 : Number(formState.baseBid),
        bidIncrement: formState.bidIncrement === '' ? 0 : Number(formState.bidIncrement),
        playersPerTeam,
        numberOfTeams,
        auctionProcess: formState.auctionProcess || 'auto',
        streamingMode: formState.streamingMode,
        streamProvider:
          formState.streamingMode === STREAMING_MODES.DIRECT_MOBILE
            ? formState.streamProvider
            : formState.streamingMode === STREAMING_MODES.OBS
              ? ''
              : 'LOCAL_PREVIEW',
        streamUrl: formState.youtubeLiveLink,
        youtubeLiveLink: formState.youtubeLiveLink,
        livePlaybackUrl: formState.livePlaybackUrl,
        rtmpIngestUrl: formState.rtmpIngestUrl,
        rtmpStreamKey: formState.rtmpStreamKey,
        youtubeIngestUrl: formState.youtubeIngestUrl,
        youtubeStreamKey: formState.youtubeStreamKey,
        youtubeVideoId: formState.youtubeVideoId,
        streamingConfig: {
          mode: formState.streamingMode,
          provider: formState.streamProvider,
          overlayMode: 'burn_in',
          youtube: {
            ingestUrl: formState.youtubeIngestUrl,
            streamKey: formState.youtubeStreamKey,
            playbackUrl: formState.youtubeLiveLink,
            videoId: formState.youtubeVideoId,
          },
          obs: {
            ingestUrl: formState.rtmpIngestUrl,
            streamKey: formState.rtmpStreamKey,
            playbackUrl: formState.livePlaybackUrl,
          },
        },
        teams: teams.slice(0, numberOfTeams).map((team, index) => ({
          name: team.name?.trim() || `Team ${index + 1}`,
          logoUrl: team.logoUrl || team.logo || '',
          ownerId: team.ownerId || '',
          captain: team.captain || '',
          retainers: Array.from(new Set((team.retainers || []).filter(Boolean))),
        })),
      };

      const response = await saveLiveAuctionSettings(selectedTournamentId, payload, token);
      const settings = response.settings || {};
      const initialTeams = buildInitialTeamState(settings.teams || payload.teams, settings.numberOfTeams || numberOfTeams);

      setFormState({
        auctionDateTime: formatDateTimeLocal(settings.auctionDateTime) || formState.auctionDateTime,
        pursePerTeam: settings.pursePerTeam ?? payload.pursePerTeam,
        baseBid: settings.baseBid ?? payload.baseBid,
        bidIncrement: settings.bidIncrement ?? payload.bidIncrement,
        playersPerTeam: settings.playersPerTeam ?? payload.playersPerTeam,
        auctionProcess: settings.auctionProcess || payload.auctionProcess,
        streamingMode: settings.streamingMode || payload.streamingMode,
        streamProvider: String(settings.streamProvider || payload.streamProvider || 'youtube').toUpperCase(),
        youtubeLiveLink: settings.youtubeLiveLink ?? payload.youtubeLiveLink,
        livePlaybackUrl: settings.livePlaybackUrl ?? payload.livePlaybackUrl,
        rtmpIngestUrl: settings.rtmpIngestUrl ?? payload.rtmpIngestUrl,
        rtmpStreamKey: settings.rtmpStreamKey ?? payload.rtmpStreamKey,
        youtubeIngestUrl: settings.youtubeIngestUrl ?? payload.youtubeIngestUrl,
        youtubeStreamKey: settings.youtubeStreamKey ?? payload.youtubeStreamKey,
        youtubeVideoId: settings.youtubeVideoId ?? payload.youtubeVideoId,
      });
      setRelayStatus(settings.relayStatus || relayStatus || { running: false });
      setNumberOfTeams(initialTeams.teamCount);
      setTeams(initialTeams.teams);
      setTeamSearchTerms(initialTeams.teamSearchTerms);
      setStatusMessage('Live auction settings saved successfully.');
    } catch (error) {
      setErrorMessage(error.message || 'Unable to save live auction settings right now.');
    } finally {
      setIsSaving(false);
    }
  };

  const auctionManagementPath = getManagementPath(selectedTournamentId || tournamentId);
  const publicAuctionPath = getPublicLiveAuctionPath(selectedTournamentId);
  const publicAuctionUrl = useMemo(() => toAbsolutePublicUrl(publicAuctionPath), [publicAuctionPath]);
  const isDirectMode = formState.streamingMode === STREAMING_MODES.DIRECT_MOBILE;
  const isObsMode = formState.streamingMode === STREAMING_MODES.OBS;

  const handleCopyPublicLink = async () => {
    if (!publicAuctionUrl) {
      setCopyLinkStatus('Select a tournament to copy the public link.');
      return;
    }

    try {
      await copyTextToClipboard(publicAuctionUrl);
      setCopyLinkStatus('Public link copied.');
    } catch (_error) {
      setCopyLinkStatus('Unable to copy the public link.');
    }
  };

  const handleGenerateYouTube = async () => {
    if (!selectedTournamentId || !token) return;
    setIsGeneratingYouTube(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const response = await generateLiveAuctionYouTubeStream(selectedTournamentId, token);
      const settings = response?.settings || {};
      setFormState((prev) => ({
        ...prev,
        youtubeLiveLink: settings.youtubeLiveLink || response.livePlaybackUrl || response.streamUrl || prev.youtubeLiveLink,
        livePlaybackUrl: settings.livePlaybackUrl || response.livePlaybackUrl || response.streamUrl || prev.livePlaybackUrl,
        youtubeIngestUrl: settings.youtubeIngestUrl || response.youtubeIngestUrl || prev.youtubeIngestUrl,
        youtubeStreamKey: settings.youtubeStreamKey || response.youtubeStreamKey || response.rtmpStreamKey || prev.youtubeStreamKey,
        youtubeVideoId: settings.youtubeVideoId || response.youtubeVideoId || prev.youtubeVideoId,
      }));
      setStatusMessage('YouTube stream details generated.');
    } catch (error) {
      setErrorMessage(error.message || 'Unable to generate YouTube stream details.');
    } finally {
      setIsGeneratingYouTube(false);
    }
  };

  return (
    <div className="py-4 d-grid gap-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h1 className="h4 mb-1">Live auction setup</h1>
          <p className="text-muted mb-0">Configure V2-ready tournament settings, teams, and retainers.</p>
        </div>
        <div className="d-flex flex-wrap justify-content-end gap-2">
          <Link
            to={publicAuctionPath || '#'}
            className={`btn btn-outline-secondary ${!publicAuctionPath ? 'disabled' : ''}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!publicAuctionPath}
            onClick={(event) => {
              if (!publicAuctionPath) event.preventDefault();
            }}
          >
            Open Public View
          </Link>
          <button type="button" className="btn btn-outline-primary" onClick={handleCopyPublicLink} disabled={!publicAuctionPath}>
            Copy Public Link
          </button>
          <Link to={auctionManagementPath} className="btn btn-outline-primary">Back to auction management</Link>
        </div>
      </div>
      {copyLinkStatus ? <p className="small mb-0 text-muted">{copyLinkStatus}</p> : null}

      <div className="card shadow-sm border-0">
        <div className="card-body d-grid gap-4">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-lg-5">
              <label htmlFor="tournamentSelect" className="form-label fw-semibold text-muted">Tournament</label>
              <select id="tournamentSelect" className="form-select" value={selectedTournamentId} onChange={handleTournamentChange} disabled={isLoadingTournaments}>
                <option value="">Select tournament</option>
                {tournaments.map((tournament) => <option key={tournament.id} value={tournament.id}>{tournament.name}</option>)}
              </select>
            </div>
            <div className="col-12 col-lg-7 text-lg-end">
              {selectedTournament ? <span className="badge bg-primary-subtle text-primary-emphasis">{selectedTournament.name}</span> : null}
            </div>
          </div>

          <form className="row g-4" onSubmit={handleSubmit}>
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label fw-semibold" htmlFor="auctionDateTime">Auction date & time</label>
              <input id="auctionDateTime" type="datetime-local" name="auctionDateTime" className="form-control" value={formState.auctionDateTime} onChange={handleInputChange} disabled={!selectedTournamentId || isLoadingSettings} />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label fw-semibold" htmlFor="pursePerTeam">Purse</label>
              <input id="pursePerTeam" type="number" min="0" step="0.01" name="pursePerTeam" className="form-control" value={formState.pursePerTeam} onChange={handleInputChange} disabled={!selectedTournamentId || isLoadingSettings} />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label fw-semibold" htmlFor="baseBid">Base bid</label>
              <input id="baseBid" type="number" min="0" step="0.01" name="baseBid" className="form-control" value={formState.baseBid} onChange={handleInputChange} disabled={!selectedTournamentId || isLoadingSettings} />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label fw-semibold" htmlFor="bidIncrement">Increment</label>
              <input id="bidIncrement" type="number" min="0" step="0.01" name="bidIncrement" className="form-control" value={formState.bidIncrement} onChange={handleInputChange} disabled={!selectedTournamentId || isLoadingSettings} />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label fw-semibold" htmlFor="playersPerTeam">Players / team</label>
              <input id="playersPerTeam" type="number" min="1" max="30" name="playersPerTeam" className="form-control" value={formState.playersPerTeam} onChange={handleInputChange} required disabled={!selectedTournamentId || isLoadingSettings} />
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold" htmlFor="auctionProcess">Auction process</label>
              <select id="auctionProcess" name="auctionProcess" className="form-select" value={formState.auctionProcess} onChange={handleInputChange}>
                <option value="auto">Auto</option>
                <option value="manual">Manual</option>
              </select>
            </div>

            <div className="col-12">
              <div className="border rounded p-3 d-grid gap-3">
                <div>
                  <h2 className="h6 mb-1">Live streaming</h2>
                  <p className="text-muted mb-0">Configure live auction broadcast mode and stream credentials.</p>
                </div>
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label className="form-label fw-semibold">Streaming mode</label>
                    <select className="form-select" name="streamingMode" value={formState.streamingMode} onChange={handleInputChange}>
                      <option value={STREAMING_MODES.DIRECT_MOBILE}>Direct Mobile</option>
                      <option value={STREAMING_MODES.OBS}>OBS</option>
                      {showLocalTestingMode ? <option value={STREAMING_MODES.LOCAL_MAC_TESTING}>Local Mac Testing</option> : null}
                    </select>
                  </div>
                  {isDirectMode ? (
                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold">Stream provider</label>
                      <select className="form-select" name="streamProvider" value={formState.streamProvider} onChange={handleInputChange}>
                        <option value={STREAM_PROVIDERS.YOUTUBE}>YouTube</option>
                        <option value={STREAM_PROVIDERS.FACEBOOK}>Facebook</option>
                      </select>
                    </div>
                  ) : null}
                  <div className="col-12 col-md-4 d-flex align-items-end">
                    <span className={`badge ${relayStatus?.running ? 'bg-success-subtle text-success-emphasis' : 'bg-secondary-subtle text-secondary-emphasis'}`}>
                      Relay: {relayStatus?.running ? 'Running' : 'Stopped'}
                    </span>
                  </div>

                  <div className="col-12 d-flex flex-wrap gap-2">
                    {formState.youtubeLiveLink ? <a href={formState.youtubeLiveLink} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-danger">Watch live</a> : null}
                    {formState.replay?.url ? <a href={formState.replay.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-success">Watch replay</a> : null}
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/public/tournaments/${selectedTournamentId}/live-auction`)}>Copy public/watch link</button>
                  </div>

                  {(isDirectMode && formState.streamProvider === STREAM_PROVIDERS.YOUTUBE) || isObsMode ? (
                    <>
                      <div className="col-12 col-md-4">
                        <label className="form-label fw-semibold">YouTube ingest URL</label>
                        <input className="form-control" name="youtubeIngestUrl" value={formState.youtubeIngestUrl} onChange={handleInputChange} />
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label fw-semibold">YouTube stream key</label>
                        <input className="form-control" name="youtubeStreamKey" value={formState.youtubeStreamKey} onChange={handleInputChange} />
                      </div>
                    </>
                  ) : null}

                  {isDirectMode && formState.streamProvider === STREAM_PROVIDERS.YOUTUBE ? (
                    <>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">YouTube live link</label>
                        <input className="form-control" name="youtubeLiveLink" value={formState.youtubeLiveLink} onChange={handleInputChange} placeholder="https://youtube.com/watch?v=..." />
                      </div>
                      <div className="col-12 col-md-3">
                        <label className="form-label fw-semibold">YouTube video ID</label>
                        <input className="form-control" name="youtubeVideoId" value={formState.youtubeVideoId} onChange={handleInputChange} />
                      </div>
                      <div className="col-12 col-md-3 d-flex align-items-end">
                        <button type="button" className="btn btn-outline-primary w-100" onClick={handleGenerateYouTube} disabled={isGeneratingYouTube || !selectedTournamentId}>
                          {isGeneratingYouTube ? 'Generating…' : 'Generate YouTube stream'}
                        </button>
                      </div>
                    </>
                  ) : null}

                  {isObsMode ? (
                    <>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">OBS ingest URL</label>
                        <input className="form-control" name="rtmpIngestUrl" value={formState.rtmpIngestUrl} onChange={handleInputChange} placeholder="rtmp://..." />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-semibold">OBS stream key</label>
                        <input className="form-control" name="rtmpStreamKey" value={formState.rtmpStreamKey} onChange={handleInputChange} />
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold" htmlFor="numberOfTeams">Number of teams</label>
              <input id="numberOfTeams" type="number" min="1" max="12" className="form-control" value={numberOfTeams} onChange={handleTeamCountChange} />
            </div>

            <div className="col-12">
              <ul className="nav nav-tabs">
                {teams.slice(0, numberOfTeams).map((team, index) => (
                  <li className="nav-item" key={`team-tab-${index}`}>
                    <button type="button" className={`nav-link ${activeTeamIndex === index ? 'active' : ''}`} onClick={() => setActiveTeamIndex(index)}>
                      <span className="d-inline-flex align-items-center gap-2">
                        {getTeamLogo(team) ? (
                          <img src={getTeamLogo(team)} alt={team.name || `Team ${index + 1}`} className="rounded border" style={{ width: 20, height: 20, objectFit: 'cover' }} />
                        ) : (
                          <span className="rounded-circle bg-light text-muted border d-inline-flex align-items-center justify-content-center" style={{ width: 20, height: 20, fontSize: '0.7rem' }} aria-hidden="true">
                            {getTeamInitial(team, index)}
                          </span>
                        )}
                        {team.name || `Team ${index + 1}`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {currentTeam ? (
              <div className="col-12">
                <div className="border rounded p-3 d-grid gap-3">
                  <div className="row g-3 align-items-start">
                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold">Team name</label>
                      <input className="form-control" value={currentTeam.name} onChange={(event) => updateTeamField(activeTeamIndex, 'name', event.target.value)} />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold">Owner</label>
                      <select
                        className="form-select"
                        value={currentTeam.ownerId || ''}
                        onChange={(event) => updateTeamField(activeTeamIndex, 'ownerId', event.target.value)}
                        disabled={ownerOptions.length === 0 || isLoadingSettings}
                      >
                        <option value="">Select owner</option>
                        {ownerOptions.map((owner) => (
                          <option key={owner.id} value={owner.id}>
                            {owner.name}
                          </option>
                        ))}
                      </select>
                      <small className="text-muted">Choose owner from tournament players added in Player Manager.</small>
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold">Captain</label>
                      <select className="form-select" value={currentTeam.captain} onChange={(event) => updateTeamField(activeTeamIndex, 'captain', event.target.value)} disabled={isLoadingPlayers}>
                        <option value="">Select captain</option>
                        {captainOptions.map((player) => <option key={player.id} value={player.id}>{player.name}</option>)}
                      </select>
                      <small className="text-muted">Choose captain from tournament players added in Player Manager.</small>
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold">Team Logo</label>
                      <div className="d-grid gap-2">
                        <input type="file" accept="image/*" className="form-control" onChange={handleLogoUpload} disabled={logoUploading || !selectedTournamentId} />
                        <input
                          type="url"
                          className="form-control"
                          placeholder="https://example.com/logo.png"
                          value={getTeamLogo(currentTeam)}
                          onChange={(event) => updateTeamField(activeTeamIndex, 'logoUrl', event.target.value)}
                        />
                        <div className="d-flex align-items-center gap-2">
                          {getTeamLogo(currentTeam) ? (
                            <img src={getTeamLogo(currentTeam)} alt={currentTeam.name || `Team ${activeTeamIndex + 1}`} className="rounded border" style={{ width: 44, height: 44, objectFit: 'cover' }} />
                          ) : (
                            <div className="rounded border bg-light d-flex align-items-center justify-content-center fw-semibold text-muted" style={{ width: 44, height: 44 }}>
                              {getTeamInitial(currentTeam, activeTeamIndex)}
                            </div>
                          )}
                          {getTeamLogo(currentTeam) ? (
                            <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => updateTeamField(activeTeamIndex, 'logoUrl', '')}>Remove</button>
                          ) : null}
                        </div>
                        {logoUploading ? <small className="text-muted">Uploading logo…</small> : null}
                        {logoError ? <small className="text-danger">{logoError}</small> : null}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-semibold mb-0">Retainers</label>
                      <input
                        type="search"
                        className="form-control"
                        placeholder="Search players"
                        style={{ maxWidth: 240 }}
                        value={teamSearchTerms[activeTeamIndex] || ''}
                        onChange={(event) => setTeamSearchTerms((prev) => prev.map((term, idx) => (idx === activeTeamIndex ? event.target.value : term)))}
                      />
                    </div>
                    <div className="border rounded" style={{ maxHeight: 240, overflowY: 'auto' }}>
                      {isLoadingPlayers ? (
                        <p className="text-muted mb-0 p-3">Loading players…</p>
                      ) : availablePlayers.length ? (
                        <ul className="list-unstyled mb-0">
                          {availablePlayers.map((player) => (
                            <li key={player.id} className="px-3 py-2 border-bottom">
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" checked={false} onChange={() => toggleRetainer(activeTeamIndex, player.id)} id={`retainer-${activeTeamIndex}-${player.id}`} />
                                <label className="form-check-label" htmlFor={`retainer-${activeTeamIndex}-${player.id}`}>{player.name}</label>
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-muted mb-0 p-3">No players available.</p>
                      )}
                    </div>
                    {currentTeam.retainers?.length ? (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {currentTeam.retainers.map((retainerId) => {
                          const player = mappedPlayers.find((candidate) => candidate.id === retainerId);
                          return (
                            <span key={retainerId} className="badge bg-primary-subtle text-primary-emphasis d-flex align-items-center gap-1">
                              {player?.name || retainerId}
                              <button type="button" className="btn-close btn-close-white btn-sm" aria-label="Remove retainer" onClick={() => toggleRetainer(activeTeamIndex, retainerId)} />
                            </span>
                          );
                        })}
                      </div>
                    ) : null}
                    {playerError ? <p className="text-danger small mt-2 mb-0">{playerError}</p> : null}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="col-12 d-flex align-items-center gap-3">
              <button type="submit" className="btn btn-primary" disabled={!selectedTournamentId || isSaving || isLoadingSettings}>
                {isSaving ? 'Saving…' : 'Save live auction'}
              </button>
              {statusMessage ? <span className="text-success">{statusMessage}</span> : null}
              {errorMessage ? <span className="text-danger">{errorMessage}</span> : null}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LiveAuctionSetupPage;
